import React, { useState } from 'react';
import api from '../utils/api';
import { getDatePreset, formatDate } from '../utils/formatters';
import DateRangeFilter, { presetLabel } from '../components/DateRangeFilter';

// Reports → Break Spots. A download, not a table: one CSV row per sold spot on
// every pending/completed break in the chosen period. The file is built
// server-side (server/services/breakSpotReport.js), which also re-reads card
// values from Airtable, so a long range can take a little while.

const COLUMNS = [
  ['Break #', 'The break number'],
  ['Break Type (Product(s))', 'The break name'],
  ['Date', 'The break date'],
  ['Time', 'When the break started (scheduled time if it never recorded a start)'],
  ['Host', 'The break host'],
  ['Average Spot Price (Break)', 'Average price of the sold spots on that break'],
  ['Username/Cust', 'The customer on the spot'],
  ['Price', 'The spot price'],
  ['NFL / NBA / MLB', 'The team(s) on the spot — all three for a Multi-Sport spot'],
  ['Hit (binary)', '1 if the spot has any card in Card(s), otherwise 0'],
  ['Card(s)', 'Card(s) as listed on the spot'],
  ['Card Value', "Sum of Airtable's Value for the case cards on the spot — blank when unknown (hand-typed cards, no case linked)"],
];

const DEFAULT_PRESET = 'month';

export default function BreakSpotReport() {
  const [range, setRange] = useState(() => getDatePreset(DEFAULT_PRESET));
  const [label, setLabel] = useState(presetLabel(DEFAULT_PRESET));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const ready = range?.startDate && range?.endDate;

  async function download() {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get('/api/reports/break-spots.csv', {
        params: { startDate: range.startDate, endDate: range.endDate },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `break-spots_${range.startDate}_to_${range.endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      setResult({
        rows: Number(res.headers['x-row-count'] || 0),
        breaks: Number(res.headers['x-break-count'] || 0),
        stale: res.headers['x-card-values-stale'] === '1',
      });
    } catch (err) {
      let message = err.message;
      // A blob response hides the server's JSON error; read it back out.
      if (err.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* keep message */ }
      }
      setError(message);
    }
    setBusy(false);
  }

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: 20 }}>Break Spots</h2>
      <DateRangeFilter
        initial={DEFAULT_PRESET}
        onChange={(r, meta) => { setRange(r); setLabel(meta?.label || null); setResult(null); setError(null); }}
      />

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title" style={{ marginBottom: 8 }}>Break Spot Report (CSV)</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          One row per sold spot on every pending or completed break
          {ready ? <> from <strong>{formatDate(range.startDate)}</strong> to <strong>{formatDate(range.endDate)}</strong></> : ''}
          {label ? ` (${label})` : ''}.
        </div>

        <button className="btn btn-primary" onClick={download} disabled={busy || !ready}>
          {busy ? 'Building CSV…' : 'Download CSV'}
        </button>
        {busy && (
          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 8 }}>
            Pulling card values from Airtable — a long range can take up to a minute.
          </div>
        )}
        {result && (
          <div style={{ fontSize: 13, marginTop: 10, color: 'var(--text-secondary)' }}>
            Downloaded {result.rows.toLocaleString()} spot{result.rows === 1 ? '' : 's'} across{' '}
            {result.breaks.toLocaleString()} break{result.breaks === 1 ? '' : 's'}.
            {result.stale && (
              <div style={{ color: 'var(--warning, #b45309)', marginTop: 4 }}>
                Airtable couldn't be reached, so some Card Values may be out of date or blank.
              </div>
            )}
          </div>
        )}
        {error && (
          <div style={{ fontSize: 13, marginTop: 10, color: 'var(--danger)' }}>Download failed: {error}</div>
        )}

        <div style={{ marginTop: 20, fontSize: 12, color: 'var(--text-secondary)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Columns</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {COLUMNS.map(([name, desc]) => (
                <tr key={name} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '5px 12px 5px 0', whiteSpace: 'nowrap', fontWeight: 500, color: 'var(--text)', verticalAlign: 'top' }}>{name}</td>
                  <td style={{ padding: '5px 0' }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
